import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import AppBackground from '../../components/ui/AppBackground.tsx';
import SpotlightCard from '../../components/ui/SpotlightCard';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert';

import MetricCard from './components/MetricCard';
import PerformanceChart from './components/PerformanceChart';
import HeatMapVisualization from './components/HeatMapVisualization';
import DetailedBreakdown from './components/DetailedBreakdown';
import GoalTracker from './components/GoalTracker';
import AchievementGallery from './components/AchievementGallery';
import ComparativeAnalytics from './components/ComparativeAnalytics';
import AIPoweredInsights from './components/AIPoweredInsights';
import TimelineNavigation from './components/NavigationComponent';

import { useDispatch, useSelector } from 'react-redux';
import {
  selectInterviews,
  selectInterviewStatistics,
  selectInterviewLoading,
  selectInterviewError,
  clearError,
  clearSuccessMessage,
  selectSuccessMessage,
} from '../../features/interview/interviewSlice';
import { fetchInterviews, fetchInterviewStatistics } from '../../features/interview/interviewThunk';
import SEO from '../SEO.jsx';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';

function getAccessToken() {
  return localStorage.getItem('access_token') || localStorage.getItem('token') || '';
}

async function fetchJson(path, { method = 'GET', signal } = {}) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal,
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

// -------------------------
// Data helpers (robust)
// -------------------------
const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const safeStr = (v, d = '') => (typeof v === 'string' && v.trim() ? v.trim() : d);

const safeStatus = (it) => String(it?.status || 'configured').toLowerCase();

const safeScore = (it, d = null) => {
  const v =
    it?.performance_score ??
    it?.overall_score ??
    it?.final_score ??
    it?.score ??
    it?.results?.performance_score ??
    it?.results?.overall_score;
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const prettyLabel = (v) =>
  String(v || '—')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getInterviewDate = (it) => it?.completed_at || it?.created_at || it?.updated_at || null;

function parseMaybeJson(v) {
  if (!v) return null;
  if (typeof v === 'object') return v;
  if (typeof v !== 'string') return null;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

function getCategoryScore(interview, key) {
  const raw =
    interview?.performance_categories ??
    interview?.results?.performance_categories ??
    interview?.analysis?.performance_categories ??
    interview?.report?.performance_categories;

  const cats = parseMaybeJson(raw) || raw;
  if (!cats || typeof cats !== 'object') return null;

  const aliases = {
    technical: ['technical', 'technical_skills', 'technicalSkill', 'technical_skills_score'],
    communication: ['communication', 'communication_skills', 'clarity', 'communication_score'],
    problem_solving: ['problem_solving', 'problemSolving', 'problem_solving_score', 'analytical_thinking'],
    behavioral: ['behavioral', 'cultural_fit', 'culture_fit', 'behavioral_score'],
  };

  const keys = aliases[key] || [key];
  for (const k of keys) {
    const node = cats?.[k];
    if (node == null) continue;
    if (typeof node === 'number') return node;
    if (typeof node === 'object') {
      const score = Number(node?.score ?? node?.value ?? node?.percentage);
      if (Number.isFinite(score)) return score;
    }
  }
  return null;
}

function getInterviewMeta(it) {
  const cfg = it?.configuration || it?.config || {};
  return {
    type: safeStr(it?.interview_type || cfg?.interview_type || it?.type || 'unknown'),
    role: safeStr(it?.selected_role || cfg?.selected_role || it?.role || ''),
    industry: safeStr(it?.selected_industry || cfg?.selected_industry || it?.industry || ''),
    difficulty: safeStr(it?.ai_difficulty || cfg?.ai_difficulty || it?.difficulty || ''),
    persona: safeStr(it?.ai_persona_name || cfg?.ai_persona_name || it?.persona || ''),
    format: safeStr(it?.selected_format || cfg?.selected_format || it?.format || ''),
    companyType: safeStr(it?.selected_company_type || cfg?.selected_company_type || it?.company_type || ''),
    duration: safeNum(it?.duration ?? cfg?.duration ?? it?.duration_minutes ?? cfg?.duration_minutes, null),
  };
}

function bucketHour(hour) {
  if (hour < 6) return 'Night';
  if (hour < 12) return 'Morning';
  if (hour < 18) return 'Afternoon';
  return 'Evening';
}

function computeStreak(interviews) {
  const days = new Set(
    (interviews || [])
      .filter((i) => safeStatus(i) === 'completed' && getInterviewDate(i))
      .map((i) => {
        const d = new Date(getInterviewDate(i));
        return new Date(d.getFullYear(), d.getMonth(), d.getDate())
          .toISOString()
          .slice(0, 10);
      })
  );

  let streak = 0;
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);

  while (days.has(cursor.toISOString().slice(0, 10))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  return streak;
}

function computeImprovementRate(interviews) {
  const completed = (interviews || [])
    .filter((i) => safeStatus(i) === 'completed' && safeScore(i) != null && getInterviewDate(i))
    .sort((a, b) => new Date(getInterviewDate(a)) - new Date(getInterviewDate(b)));

  if (completed.length < 4) return 0;

  const mid = Math.floor(completed.length / 2);
  const first = completed.slice(0, mid);
  const second = completed.slice(mid);

  const avg = (arr) => arr.reduce((sum, x) => sum + safeScore(x, 0), 0) / Math.max(1, arr.length);
  const a = avg(first);
  const b = avg(second);

  if (!a) return 0;
  return Math.round(((b - a) / a) * 1000) / 10;
}

function buildPerformanceTrends(interviews, timeRange) {
  const now = new Date();
  const completed = (interviews || []).filter((i) => safeStatus(i) === 'completed' && getInterviewDate(i));

  const periods = [];
  const pushPeriod = (label, start, end) => {
    const items = completed.filter((it) => {
      const d = new Date(getInterviewDate(it));
      return d >= start && d <= end;
    });

    const avg = (keyFn) => {
      const vals = items.map(keyFn).filter((v) => v != null && Number.isFinite(Number(v)));
      if (!vals.length) return 0;
      return Math.round((vals.reduce((sum, x) => sum + Number(x), 0) / vals.length) * 10) / 10;
    };

    periods.push({
      period: label,
      score: avg((it) => safeScore(it)),
      sessions: items.length,
      technical: avg((it) => getCategoryScore(it, 'technical')),
      communication: avg((it) => getCategoryScore(it, 'communication')),
      problem_solving: avg((it) => getCategoryScore(it, 'problem_solving')),
      behavioral: avg((it) => getCategoryScore(it, 'behavioral')),
    });
  };

  if (timeRange === 'week') {
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
      const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
      pushPeriod(d.toLocaleDateString('en-US', { weekday: 'short' }), start, end);
    }
  } else if (timeRange === 'month') {
    for (let i = 3; i >= 0; i--) {
      const end = new Date(now);
      end.setDate(end.getDate() - i * 7);
      end.setHours(23, 59, 59, 999);
      const start = new Date(end);
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      pushPeriod(`Week ${4 - i}`, start, end);
    }
  } else if (timeRange === 'quarter') {
    for (let i = 2; i >= 0; i--) {
      const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);
      pushPeriod(start.toLocaleDateString('en-US', { month: 'short' }), start, end);
    }
  } else {
    for (let i = 11; i >= 0; i--) {
      const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);
      pushPeriod(start.toLocaleDateString('en-US', { month: 'short' }), start, end);
    }
  }

  return periods;
}

function buildSkillAnalysis(interviews) {
  const completed = (interviews || []).filter((i) => safeStatus(i) === 'completed');
  const keys = [
    ['technical', 'Technical Skills'],
    ['communication', 'Communication'],
    ['problem_solving', 'Problem Solving'],
    ['behavioral', 'Behavioral / Culture Fit'],
  ];

  const rows = keys
    .map(([key, label]) => {
      const scores = completed
        .map((it) => getCategoryScore(it, key))
        .filter((v) => v != null && Number.isFinite(Number(v)))
        .map(Number);

      if (!scores.length) return null;

      const first = scores[0];
      const last = scores[scores.length - 1];
      const avg = scores.reduce((sum, x) => sum + x, 0) / scores.length;

      return {
        skill: label,
        key,
        score: Math.round(avg * 10) / 10,
        trend: Math.round((last - first) * 10) / 10,
        count: scores.length,
      };
    })
    .filter(Boolean);

  if (rows.length) return rows.sort((a, b) => b.score - a.score);

  // Fallback: support custom backend keys
  const acc = {};
  completed.forEach((it) => {
    const raw = it?.performance_categories ?? it?.results?.performance_categories;
    const cats = parseMaybeJson(raw) || raw;
    if (!cats || typeof cats !== 'object') return;
    Object.entries(cats).forEach(([k, v]) => {
      const score = typeof v === 'number' ? v : safeNum(v?.score ?? v?.value, null);
      if (score == null) return;
      if (!acc[k]) acc[k] = [];
      acc[k].push(score);
    });
  });

  return Object.entries(acc)
    .map(([k, scores]) => ({
      skill: prettyLabel(k),
      key: k,
      score:
        Math.round(
          (scores.reduce((sum, x) => sum + safeNum(x, 0), 0) /
            Math.max(1, scores.length)) *
            10
        ) / 10,
      trend: Math.round(((scores[scores.length - 1] ?? 0) - (scores[0] ?? 0)) * 10) / 10,
      count: scores.length,
    }))
    .sort((a, b) => b.score - a.score);
}

function buildTypeDistribution(interviews) {
  const completed = (interviews || []).filter((i) => safeStatus(i) === 'completed');
  const map = {};
  completed.forEach((it) => {
    const t = safeStr(getInterviewMeta(it).type || '').toLowerCase();
    if (!t || t === 'unknown') return;
    map[t] = (map[t] || 0) + 1;
  });
  const label = (t) => {
    if (t === 'chat') return 'Chat';
    if (t === 'phone') return 'Phone';
    if (t === 'video') return 'Video';
    if (t === 'in_person' || t === 'in-person') return 'In-person';
    return prettyLabel(t);
  };
  return Object.entries(map)
    .map(([k, v]) => ({ name: label(k), value: v, key: k }))
    .sort((a, b) => b.value - a.value);
}

function buildTimeOfDay(interviews) {
  const completed = (interviews || []).filter(
    (i) => safeStatus(i) === 'completed' && getInterviewDate(i)
  );
  const buckets = { Night: [], Morning: [], Afternoon: [], Evening: [] };
  completed.forEach((it) => {
    const d = new Date(getInterviewDate(it));
    const b = bucketHour(d.getHours());
    const score = safeScore(it, null);
    if (score != null) buckets[b].push(score);
  });
  return Object.entries(buckets).map(([k, scores]) => ({
    bucket: k,
    score: scores.length
      ? Math.round((scores.reduce((s, x) => s + x, 0) / scores.length) * 10) / 10
      : 0,
    sessions: scores.length,
  }));
}

function buildAchievements(interviews, stats, achievementStats, backendProgress) {
  const out = [];

  const completed = safeNum(stats?.completed_interviews, 0);
  const best = safeNum(stats?.highest_score, 0);
  const streak = computeStreak(interviews);
  const avg = safeNum(stats?.average_score, 0);

  const mk = (id, title, description, icon, progress, unlocked, meta = {}) => ({
    id,
    title,
    description,
    icon,
    progress: Math.max(0, Math.min(100, Math.round(progress))),
    unlocked: Boolean(unlocked),
    ...meta,
  });

  out.push(
    mk(
      'sessions_20',
      'Consistency Builder',
      'Complete 20 practice interviews.',
      'Calendar',
      (completed / 20) * 100,
      completed >= 20,
      { category: 'milestones', rarity: completed >= 20 ? 'rare' : 'common' }
    )
  );

  out.push(
    mk(
      'avg_85',
      'Score Momentum',
      'Maintain an 85+ average score.',
      'TrendingUp',
      (avg / 85) * 100,
      avg >= 85,
      { category: 'performance', rarity: avg >= 85 ? 'epic' : 'rare' }
    )
  );

  out.push(
    mk(
      'best_90',
      'Breakthrough',
      'Reach a 90+ performance score.',
      'Sparkles',
      (best / 90) * 100,
      best >= 90,
      { category: 'performance', rarity: best >= 90 ? 'epic' : 'rare' }
    )
  );

  out.push(
    mk(
      'streak_7',
      'Week Streak',
      'Practice 7 days in a row.',
      'Flame',
      (streak / 7) * 100,
      streak >= 7,
      { category: 'consistency', rarity: streak >= 7 ? 'rare' : 'common' }
    )
  );

  if (achievementStats && typeof achievementStats === 'object') {
    const map = [
      { key: 'interview_ace', title: 'Interview Ace', desc: '90+ scores milestone', icon: 'Trophy', category: 'performance' },
      { key: 'consistency', title: 'Consistency King', desc: 'Build a 30-day streak', icon: 'Crown', category: 'consistency' },
      { key: 'technical_master', title: 'Technical Master', desc: 'Improve technical averages', icon: 'Code', category: 'performance' },
      { key: 'variety', title: 'Variety Explorer', desc: 'Practice multiple formats', icon: 'Layers', category: 'diversity' },
    ];

    map.forEach((a) => {
      if (achievementStats[a.key] == null) return;
      const p = safeNum(achievementStats[a.key], 0);
      out.push(mk(`backend_${a.key}`, a.title, a.desc, a.icon, p, p >= 100, { category: a.category, rarity: p >= 100 ? 'epic' : 'rare' }));
    });
  }

  if (Array.isArray(backendProgress)) {
    backendProgress.forEach((p, idx) => {
      const title = safeStr(p?.challenge_title || p?.title || `Challenge ${idx + 1}`);
      const desc = safeStr(p?.description || 'Keep going — you are close.');
      const prog = safeNum(p?.progress_percentage ?? p?.progress ?? 0, 0);
      out.push(mk(`challenge_${p?.id || idx}`, title, desc, 'Target', prog, prog >= 100, { category: 'milestones', rarity: prog >= 100 ? 'rare' : 'common' }));
    });
  }

  const seen = new Set();
  return out.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
}

function buildComparativeData(interviews, stats, skillAnalysis) {
  const completed = (interviews || [])
    .filter((i) => safeStatus(i) === 'completed' && safeScore(i) != null && i?.completed_at)
    .sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at));

  const recent = completed.slice(-5);
  const prev = completed.slice(Math.max(0, completed.length - 10), Math.max(0, completed.length - 5));

  const avg = (arr) => arr.reduce((s, x) => s + safeNum(safeScore(x), 0), 0) / Math.max(1, arr.length);
  const recentAvg = Math.round(avg(recent) * 10) / 10;
  const prevAvg = Math.round(avg(prev) * 10) / 10;

  const typeDist = buildTypeDistribution(completed);

  return {
    baseline: {
      recentAvg,
      prevAvg,
      delta: Math.round((recentAvg - prevAvg) * 10) / 10,
      recentCount: recent.length,
      prevCount: prev.length,
    },
    byType: typeDist,
    skills: skillAnalysis || [],
    average: safeNum(stats?.average_score, 0),
  };
}

function buildAIInsights(interviews, stats, skillAnalysis, trends) {
  const insights = [];
  const streak = computeStreak(interviews);
  const improvement = computeImprovementRate(interviews);
  const avg = safeNum(stats?.average_score, 0);
  const best = safeNum(stats?.highest_score, 0);

  if (improvement >= 10) {
    insights.push({
      type: 'positive',
      title: 'Momentum is strong',
      description: `Your average performance improved by ${improvement}% across your timeline. Keep the cadence — it’s working.`,
      priority: 'high',
      icon: 'TrendingUp',
    });
  } else if (improvement <= -5) {
    insights.push({
      type: 'warning',
      title: 'Scores dipped recently',
      description:
        'Your recent sessions are below your earlier baseline. Focus on fundamentals + do one targeted session on your weakest area.',
      priority: 'high',
      icon: 'AlertTriangle',
    });
  }

  if (streak >= 5) {
    insights.push({
      type: 'positive',
      title: 'Consistency advantage',
      description: `You’re on a ${streak}-day streak. Consistency is the fastest way to stabilize performance under pressure.`,
      priority: 'medium',
      icon: 'Flame',
    });
  } else {
    insights.push({
      type: 'neutral',
      title: 'Build your rhythm',
      description: 'Aim for short daily practice (10–15 minutes). Consistency matters more than long sessions.',
      priority: 'medium',
      icon: 'Calendar',
    });
  }

  const lowest = [...(skillAnalysis || [])].sort((a, b) => a.score - b.score)[0];
  if (lowest && lowest.score < 70) {
    insights.push({
      type: 'warning',
      title: `Focus area: ${lowest.skill}`,
      description: `This is your lowest-scoring category (${lowest.score}). Do 2–3 focused drills this week and re-test.`,
      priority: 'high',
      icon: 'Target',
    });
  }

  if (best >= 90 && avg < 85) {
    insights.push({
      type: 'neutral',
      title: 'Peak potential is proven',
      description:
        'You’ve already hit 90+. The next win is consistency: reduce variance by rehearsing structure and timing.',
      priority: 'medium',
      icon: 'Sparkles',
    });
  }

  if (Array.isArray(trends) && trends.length >= 4) {
    const first = safeNum(trends[0]?.score, 0);
    const last = safeNum(trends[trends.length - 1]?.score, 0);
    const delta = Math.round((last - first) * 10) / 10;
    if (delta >= 5) {
      insights.push({
        type: 'positive',
        title: 'Trend is climbing',
        description: `Your score trend is up by ${delta} points over this range.`,
        priority: 'low',
        icon: 'ArrowUpRight',
      });
    }
  }

  const score = (p) => (p === 'high' ? 3 : p === 'medium' ? 2 : 1);
  const seen = new Set();
  return insights
    .filter((x) => (seen.has(x.title) ? false : (seen.add(x.title), true)))
    .sort((a, b) => score(b.priority) - score(a.priority));
}

const ProgressAnalyticsInsights = () => {
  const dispatch = useDispatch();

  const interviews = useSelector(selectInterviews);
  const statistics = useSelector(selectInterviewStatistics);
  const loadingInterviews = useSelector(selectInterviewLoading);
  const errorInterviews = useSelector(selectInterviewError);
  const successMessage = useSelector(selectSuccessMessage);

  const [selectedTimeRange, setSelectedTimeRange] = useState('month');
  const [isExporting, setIsExporting] = useState(false);

  const [dashboard, setDashboard] = useState(null);
  const [achievementStats, setAchievementStats] = useState(null);
  const [usage, setUsage] = useState(null); // FIX: bring quota card from PastInterviews
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [processedData, setProcessedData] = useState({
    performanceTrends: [],
    practiceActivity: [],
    sessionBreakdown: [],
    skillAnalysis: [],
    achievementData: [],
    comparativeData: {},
    aiInsights: [],
    typeDistribution: [],
    timeOfDay: [],
  });

  // Fetch interviews & basic stats
  useEffect(() => {
    dispatch(fetchInterviews());
    dispatch(fetchInterviewStatistics());
  }, [dispatch]);

  // FIX: abortable advanced analytics fetch + usage in parallel
  useEffect(() => {
    const ctrl = new AbortController();
    let mounted = true;
    (async () => {
      try {
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        const [dash, ach] = await Promise.all([
          fetchJson('/api/interview/analytics/dashboard', { signal: ctrl.signal }),
          fetchJson('/api/interview/analytics/achievement-stats', { signal: ctrl.signal }),
        ]);
        if (!mounted) return;
        setDashboard(dash);
        setAchievementStats(ach);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        if (!mounted) return;
        setAnalyticsError(e?.message || 'Failed to load analytics');
      } finally {
        if (mounted) setAnalyticsLoading(false);
      }

      // Quota is best-effort — free users may not have a subscription endpoint
      try {
        const u = await fetchJson('/api/subscriptions/my-subscription/usage', { signal: ctrl.signal });
        if (mounted) setUsage(u?.usage || null);
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, []);

  // Derivations
  const completedSessions = useMemo(
    () => (interviews || []).filter((i) => safeStatus(i) === 'completed'),
    [interviews]
  );

  const effectiveStatistics = useMemo(() => {
    const list = interviews || [];
    const completed = list.filter((i) => safeStatus(i) === 'completed');
    const scores = completed.map((i) => safeScore(i)).filter((v) => v != null);

    return {
      total_interviews: statistics?.total_interviews ?? list.length,
      completed_interviews: statistics?.completed_interviews ?? completed.length,
      in_progress: statistics?.in_progress ?? list.filter((i) => safeStatus(i) === 'in_progress').length,
      average_score:
        statistics?.average_score ??
        (scores.length ? Math.round((scores.reduce((sum, x) => sum + x, 0) / scores.length) * 10) / 10 : 0),
      highest_score: statistics?.highest_score ?? (scores.length ? Math.max(...scores) : 0),
    };
  }, [statistics, interviews]);

  useEffect(() => {
    if (!interviews) return;

    const skillAnalysis = buildSkillAnalysis(interviews);
    const performanceTrends = buildPerformanceTrends(interviews, selectedTimeRange);
    const typeDistribution = buildTypeDistribution(interviews);
    const timeOfDay = buildTimeOfDay(interviews);

    const achievementData = buildAchievements(
      interviews,
      effectiveStatistics,
      achievementStats,
      dashboard?.achievements
    );
    const comparativeData = buildComparativeData(interviews, effectiveStatistics, skillAnalysis);
    const aiInsights = buildAIInsights(interviews, effectiveStatistics, skillAnalysis, performanceTrends);

    // FIX: pass ALL interviews (not just completed) to DetailedBreakdown so the
    // session list can show every status. The component does its own filtering.
    setProcessedData({
      performanceTrends,
      practiceActivity: [],
      sessionBreakdown: interviews,
      skillAnalysis,
      achievementData,
      comparativeData,
      aiInsights,
      typeDistribution,
      timeOfDay,
    });
  }, [interviews, effectiveStatistics, achievementStats, dashboard, selectedTimeRange]);

  const streak = useMemo(() => computeStreak(interviews), [interviews]);
  const improvementRate = useMemo(() => computeImprovementRate(interviews), [interviews]);
  const completionRate = useMemo(() => {
    const total = safeNum(effectiveStatistics?.total_interviews, 0);
    const completed = safeNum(effectiveStatistics?.completed_interviews, 0);
    if (!total) return 0;
    return Math.round((completed / total) * 100);
  }, [effectiveStatistics]);

  const kpis = useMemo(() => {
    const total = safeNum(effectiveStatistics?.total_interviews, 0);
    const completed = safeNum(effectiveStatistics?.completed_interviews, 0);
    const avg = Math.round(safeNum(effectiveStatistics?.average_score, 0));
    const best = Math.round(safeNum(effectiveStatistics?.highest_score, 0));

    const weekTrend = safeNum(
      dashboard?.weekly_trend?.percentage_change ?? dashboard?.weekly_trend,
      null
    );
    const weekTrendLabel =
      weekTrend == null ? '—' : `${weekTrend > 0 ? '+' : ''}${Math.round(weekTrend)}%`;

    // FIX: improvementRate of 0 was being treated as "positive" → green chip.
    // It's actually neutral: nothing changed.
    const improvementType =
      improvementRate > 0 ? 'positive' : improvementRate < 0 ? 'negative' : 'neutral';
    const improvementChange =
      improvementRate > 0
        ? `+${improvementRate}%`
        : improvementRate < 0
          ? `${improvementRate}%`
          : '0%';

    return [
      {
        title: 'Total Sessions',
        value: String(total),
        subtitle: 'All-time',
        change: total === completed ? '—' : `${total - completed} pending`,
        changeType: 'neutral',
        icon: 'Layers',
        color: 'accent',
      },
      {
        title: 'Completed',
        value: String(completed),
        subtitle: `${completionRate}% completion`,
        change: completed > 0 ? `${completionRate}%` : '—',
        changeType: completionRate >= 70 ? 'positive' : 'neutral',
        icon: 'BadgeCheck',
        color: 'success',
      },
      {
        title: 'Average Score',
        value: String(avg),
        subtitle: 'Across completed',
        change: improvementChange,
        changeType: improvementType,
        icon: 'TrendingUp',
        color: 'success',
      },
      {
        title: 'Best Score',
        value: String(best),
        subtitle: 'Peak performance',
        change: weekTrendLabel,
        changeType:
          weekTrend == null ? 'neutral' : weekTrend > 0 ? 'positive' : weekTrend < 0 ? 'negative' : 'neutral',
        icon: 'Sparkles',
        color: 'warning',
      },
      {
        title: 'Improvement',
        value: String(Math.abs(improvementRate)),
        subtitle: 'Baseline → recent',
        change: improvementChange,
        changeType: improvementType,
        icon: 'Target',
        suffix: '%',
        color: 'warning',
      },
      {
        title: 'Streak',
        value: String(streak),
        subtitle: 'Days in a row',
        change: streak ? `${streak}d` : '—',
        changeType: streak ? 'positive' : 'neutral',
        icon: 'Flame',
        suffix: 'd',
        color: 'error',
      },
    ];
  }, [effectiveStatistics, completionRate, improvementRate, streak, dashboard]);

  const timeRanges = [
    { value: 'week', label: '7D' },
    { value: 'month', label: '4W' },
    { value: 'quarter', label: '3M' },
    { value: 'year', label: '12M' },
  ];

  // Quota progress for the new banner card
  const usagePct = useMemo(() => {
    if (!usage) return null;
    const used = safeNum(usage.interviews_used, 0);
    const limit = safeNum(usage.interviews_limit, 0);
    if (!limit) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  }, [usage]);

  const handleExportReport = async () => {
    setIsExporting(true);
    try {
      await new Promise((r) => setTimeout(r, 650));
    } finally {
      setIsExporting(false);
    }
  };

  // FIX: delete callback for DetailedBreakdown's session modal. Wires the
  // "Past Interviews" delete affordance into this page.
  const handleDeleteSession = async (id) => {
    setDeleteError(null);
    try {
      await fetchJson(`/api/interview/${id}`, { method: 'DELETE' });
      // Optimistically refresh the list (Redux will refetch but this keeps UX snappy)
      dispatch(fetchInterviews());
      dispatch(fetchInterviewStatistics());
    } catch (e) {
      setDeleteError(e?.message || 'Delete failed');
      throw e; // let modal know
    }
  };

  return (
    <>
      <Helmet>
        <title>Progress Analytics &amp; Insights - InterviewGenie</title>
        <meta
          name="description"
          content="Track your interview performance with comprehensive analytics, trends, and insights to guide your preparation journey."
        />
      </Helmet>
        <SEO
          title="Interview Progress Analytics — InterviewGenieAI"
          description="Track your interview scores, confidence, answer quality, progress history, badges, and performance analytics inside InterviewGenieAI."
          canonical="https://interviewgenieai.com/analytics"
          noindex={true}
        />

      <div className="min-h-screen w-full text-white relative overflow-hidden">
        <AppBackground>
          <main className="relative pt-24 pb-16 z-10">
            <div className="hidden lg:block">
              <TimelineNavigation />
            </div>

            <div className="mx-auto w-full max-w-[1200px] px-6 lg:px-8">
              {/* HERO */}
              <div className="mb-8">
                <SpotlightCard
                  className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_24px_70px_-40px_rgba(0,0,0,0.85)] p-6 md:p-7"
                  spotlightColor="rgba(99,102,241,0.18)"
                >
                  <div className="pointer-events-none absolute inset-0">
                    <div className="absolute -top-24 left-1/2 h-72 w-[48rem] -translate-x-1/2 rounded-full bg-gradient-to-r from-violet-500/20 via-sky-500/18 to-emerald-500/16 blur-3xl" />
                    <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_55%_0%,rgba(255,255,255,0.10),transparent_60%)]" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/10 to-black/45" />
                  </div>

                  <div className="relative z-10">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      <div className="flex items-start gap-4 min-w-0">
                        <div className="h-14 w-14 rounded-3xl bg-white/8 border border-white/12 backdrop-blur-xl flex items-center justify-center overflow-hidden shrink-0">
                          <img
                            src={'/progress_ill7tc.png'}
                            className="w-10 h-10 object-contain opacity-95"
                            alt="progress"
                          />
                        </div>

                        <div className="min-w-0">
                          <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] text-white/70 backdrop-blur-xl">
                            <span className="h-2 w-2 rounded-full bg-emerald-300 shadow-[0_0_16px_rgba(52,211,153,0.55)]" />
                            Live analytics • Skill signals • Session deep-dive • Actionable next steps
                            <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/70">
                              {analyticsLoading ? 'Updating…' : 'Synced'}
                            </span>
                          </div>

                          <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-white">
                            Progress Analytics &amp; Insights
                          </h1>
                          <p className="text-white/60 mt-2 max-w-2xl">
                            See exactly what’s improving, what’s blocking your score, and what to
                            practice next — with full session-level evidence.
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <div className="inline-flex items-center rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-1">
                          {timeRanges.map((r) => {
                            const active = selectedTimeRange === r.value;
                            return (
                              <button
                                key={r.value}
                                type="button"
                                onClick={() => setSelectedTimeRange(r.value)}
                                aria-pressed={active}
                                className={[
                                  'px-3 py-2 text-xs font-semibold rounded-xl transition',
                                  active
                                    ? 'bg-white/10 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.10)]'
                                    : 'text-white/60 hover:text-white/85 hover:bg-white/[0.06]',
                                ].join(' ')}
                              >
                                {r.label}
                              </button>
                            );
                          })}
                        </div>

                        <Button
                          onClick={handleExportReport}
                          className="!rounded-2xl !bg-white/[0.06] hover:!bg-white/[0.10] !border !border-white/10 !text-white/90"
                          disabled={isExporting}
                        >
                          <span className="inline-flex items-center gap-2">
                            <Icon
                              name={isExporting ? 'Loader2' : 'Download'}
                              size={16}
                              className={isExporting ? 'animate-spin' : ''}
                            />
                            {isExporting ? 'Preparing…' : 'Export report'}
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </SpotlightCard>
              </div>

              {/* QUOTA — added from PastInterviews */}
              {usage ? (
                <div className="mb-6">
                  <SpotlightCard
                    className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.7)] p-5"
                    spotlightColor="rgba(245,158,11,0.14)"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-white/85 flex-wrap">
                          <Icon name="Zap" size={16} className="text-amber-300" />
                          <span className="font-semibold">Monthly Interview Quota</span>
                          <span className="text-xs text-white/45">
                            (resets in {usage.days_until_reset ?? 0} days)
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-white/60">
                          You’ve used{' '}
                          <span className="text-white font-semibold tabular-nums">
                            {usage.interviews_used}
                          </span>{' '}
                          /{' '}
                          <span className="text-white font-semibold tabular-nums">
                            {usage.interviews_limit}
                          </span>{' '}
                          interviews this month.
                        </div>
                        <div className="mt-3 h-2 w-full rounded-full bg-white/10 overflow-hidden">
                          <div
                            className={[
                              'h-full rounded-full transition-all duration-500',
                              (usagePct ?? 0) >= 90
                                ? 'bg-gradient-to-r from-rose-400/90 to-amber-400/90'
                                : 'bg-gradient-to-r from-emerald-400/90 via-sky-400/90 to-violet-400/90',
                            ].join(' ')}
                            style={{ width: `${usagePct ?? 0}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 shrink-0">
                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                          <div className="text-[11px] text-white/55">Remaining</div>
                          <div className="mt-1 text-lg font-semibold text-white tabular-nums">
                            {usage.interviews_remaining ??
                              Math.max(
                                (usage.interviews_limit || 0) - (usage.interviews_used || 0),
                                0
                              )}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                          <div className="text-[11px] text-white/55">Usage</div>
                          <div className="mt-1 text-lg font-semibold text-white tabular-nums">
                            {usagePct ?? 0}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </SpotlightCard>
                </div>
              ) : null}

              {/* Alerts */}
              {errorInterviews && (
                <div className="mb-6">
                  <Alert variant="destructive">
                    <AlertTitle>Couldn’t load interviews</AlertTitle>
                    <AlertDescription>{String(errorInterviews)}</AlertDescription>
                  </Alert>
                </div>
              )}

              {analyticsError && (
                <div className="mb-6">
                  <Alert>
                    <AlertTitle>Advanced analytics unavailable</AlertTitle>
                    <AlertDescription>
                      {analyticsError} — showing best-effort insights from your sessions.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {deleteError && (
                <div className="mb-6">
                  <Alert variant="destructive">
                    <AlertTitle>Couldn’t delete</AlertTitle>
                    <AlertDescription>{deleteError}</AlertDescription>
                  </Alert>
                </div>
              )}

              {successMessage && (
                <div className="mb-6">
                  <Alert>
                    <AlertTitle>Success</AlertTitle>
                    <AlertDescription>{successMessage}</AlertDescription>
                  </Alert>
                </div>
              )}

              {/* METRICS */}
              <section id="metrics" className="mb-10">
                <div className="flex items-end justify-between gap-4 mb-4">
                  <div>
                    <div className="text-sm font-semibold text-white/80">Overview</div>
                    <div className="text-xs text-white/55">
                      Your key KPIs and what’s moving the needle.
                    </div>
                  </div>
                  {loadingInterviews && (
                    <div className="inline-flex items-center gap-2 text-xs text-white/55">
                      <Icon name="Loader2" size={14} className="animate-spin" />
                      Loading…
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {kpis.map((m) => (
                    <MetricCard key={m.title} {...m} />
                  ))}
                </div>
              </section>

              {/* PERFORMANCE */}
              <section id="performance" className="mb-10">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                  <div className="lg:col-span-3">
                    <PerformanceChart
                      data={processedData.performanceTrends}
                      timeRange={selectedTimeRange}
                    />
                  </div>

                  <div className="lg:col-span-1 space-y-6">
                    <SpotlightCard
                      className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)] p-5"
                      spotlightColor="rgba(16,185,129,0.14)"
                    >
                      <div className="pointer-events-none absolute inset-0">
                        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-emerald-400/12 blur-[80px]" />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/40" />
                      </div>

                      <div className="relative z-10">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-semibold text-white">Session mix</div>
                          <div className="text-xs text-white/55 tabular-nums">
                            {completedSessions.length} completed
                          </div>
                        </div>

                        <div className="mt-4 space-y-2">
                          {(processedData.typeDistribution || []).slice(0, 5).map((t) => {
                            const total =
                              (processedData.typeDistribution || []).reduce(
                                (s, x) => s + safeNum(x.value, 0),
                                0
                              ) || 1;
                            const pct = Math.round((safeNum(t.value, 0) / total) * 100);
                            return (
                              <div key={t.key} className="flex items-center gap-3">
                                <div className="w-20 text-xs text-white/65 truncate">
                                  {t.name}
                                </div>
                                <div className="flex-1 h-2 rounded-full bg-white/8 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-r from-violet-400/70 via-sky-400/70 to-emerald-400/70"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <div className="w-10 text-right text-xs text-white/65 tabular-nums">
                                  {pct}%
                                </div>
                              </div>
                            );
                          })}
                          {!processedData.typeDistribution?.length ? (
                            <div className="text-xs text-white/45">
                              Mix appears after a few completed sessions.
                            </div>
                          ) : null}
                        </div>

                        <div className="mt-5 pt-4 border-t border-white/10">
                          <div className="text-xs text-white/55 mb-2">
                            Best time-of-day performance
                          </div>
                          {processedData.timeOfDay?.length ? (
                            <div className="space-y-2">
                              {processedData.timeOfDay
                                .slice()
                                .filter((b) => b.sessions > 0)
                                .sort((a, b) => b.score - a.score)
                                .slice(0, 2)
                                .map((b) => (
                                  <div
                                    key={b.bucket}
                                    className="flex items-center justify-between text-xs"
                                  >
                                    <span className="text-white/70">{b.bucket}</span>
                                    <span className="text-white/85 font-semibold tabular-nums">
                                      {b.score || 0}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <div className="text-xs text-white/50">
                              Complete a few sessions to unlock signals.
                            </div>
                          )}
                        </div>
                      </div>
                    </SpotlightCard>

                    <GoalTracker
                      interviews={interviews}
                      statistics={effectiveStatistics}
                      skillAnalysis={processedData.skillAnalysis}
                    />
                  </div>
                </div>
              </section>

              {/* HEATMAP */}
              <section id="heatmap" className="mb-10">
                <HeatMapVisualization interviews={interviews} />
              </section>

              {/* BREAKDOWN — now with PastInterviews-style session detail + delete */}
              <section id="breakdown" className="mb-10">
                <DetailedBreakdown
                  sessions={processedData.sessionBreakdown}
                  statistics={effectiveStatistics}
                  skillAnalysis={processedData.skillAnalysis}
                  timeOfDay={processedData.timeOfDay}
                  typeDistribution={processedData.typeDistribution}
                  onDeleteSession={handleDeleteSession}
                />
              </section>

              {/* ACHIEVEMENTS */}
              <section id="achievements" className="mb-10">
                <AchievementGallery achievements={processedData.achievementData} />
              </section>

              {/* COMPARATIVE */}
              <section id="comparative" className="mb-10">
                <ComparativeAnalytics
                  data={processedData.comparativeData}
                  skillAnalysis={processedData.skillAnalysis}
                />
              </section>

              {/* INSIGHTS */}
              <section id="insights" className="mb-8">
                <AIPoweredInsights
                  insights={processedData.aiInsights}
                  interviews={interviews}
                  statistics={effectiveStatistics}
                />
              </section>

              <div className="text-center text-xs text-white/45 pb-4">
                Analytics update as you complete interviews. The more sessions you do, the more
                accurate your skill signals become.
              </div>
            </div>
          </main>
        </AppBackground>
      </div>
    </>
  );
};

export default ProgressAnalyticsInsights;
